import { Stack } from "@fluentui/react";
import { Body1, makeStyles } from "@fluentui/react-components";
import {
  Apps24Regular,
  ArrowSync24Regular,
  ChevronLeft24Regular,
  ChevronRight24Regular,
  DocumentText24Regular,
  New16Regular,
  People24Regular,
  PeopleAudience24Regular,
  PersonSupport24Regular,
  PictureInPicture24Regular,
  VideoPersonSparkle24Regular,
  HeartPulse24Regular,
  DataTrending24Regular,
  PeopleSearch24Regular,
  BranchFork24Regular,
  ChevronRight16Regular,
  ChevronLeft16Regular,
  PeopleTeam24Regular
} from "@fluentui/react-icons";
import { Paper } from "@mui/material";
import * as React from "react";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import CarouselImage1 from "../assets/images-homepage/carousel-img-1.png";
import CarouselImage2 from "../assets/images-homepage/carousel-img-2.png";
import CarouselImage3 from "../assets/images-homepage/carousel-img-3.jpg";
import CarouselImage4 from "../assets/images-homepage/carousel-img-4.png";
import CarouselImage5 from "../assets/images-homepage/carousel-img-5.jpg";
import FavoritesSection from "../components/Home/FavoritesSection/FavoritesSection";
import { topCustomers, topProducts, topReports } from "../components/Home/HomePage.constants";
import { logPageView } from "../helper/AppInsightsHelper";
import { useGetRecentlyViewedItemsQuery, useGetUIEnablementsSettingsQuery } from "../slices/Home.slice";
import { buildDisabledFeatureLookup } from "../Utils/js-common";

const useStyles = makeStyles({
  announcementSection: {
    position: "relative",
    width: "48%",
    height: "400px",
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    ":hover": {
      cursor: "pointer"
    }
  },
  slide: {
    position: "absolute",
    width: "100%",
    height: "100%",
    opacity: 0,
    transition: "opacity 0.5s ease-in-out",
    cursor: "pointer",
    display: "flex",
    flexDirection: "row-reverse",
    justifyContent: "flex-end",
    background: "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)",
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    objectFit: "cover" // Ensure the image covers the entire div
  },
  activeSlide: {
    opacity: 1
  },
  slideContent: {
    display: "flex",
    flexDirection: "column",
    color: "white",
    zIndex: 1
  },
  slideTitle: {
    fontSize: "30px",
    marginLeft: "20px",
    fontWeight: "bold",
    color: "white",
    marginBottom: 0
  },
  slideDescription: {
    marginTop: 0,
    fontSize: "18px",
    marginLeft: "20px",
    color: "white"
  },
  navigationButton: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    background: "rgba(255, 255, 255, 0.9)",
    borderRadius: "50%",
    width: "40px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    zIndex: 2,
    border: "none",
    ":hover": {
      background: "white"
    }
  },
  prevButton: {
    left: "20px"
  },
  nextButton: {
    right: "20px"
  },
  tileStyles: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column"
  },
  tileDescription: {
    fontWeight: 500,
    fontSize: "0.975rem",
    lineHeight: "1.15rem",
    textAlign: "center"
  },
  comingSoonTileDescription: {
    fontWeight: 500,
    fontSize: "0.975rem",
    lineHeight: "0.2rem",
    textAlign: "center",
    ":hover": {
      cursor: "not-allowed"
    }
  },
  newTileDescription: {
    fontWeight: 500,
    fontSize: "0.975rem",
    textAlign: "center"
  },
  caption: {
    fontWeight: 50,
    fontSize: "0.80rem",
    textAlign: "center",
    paddingTop: "2px",
    ":hover": {
      cursor: "not-allowed"
    }
  },
  paperStyles: {
    padding: "30px 20px 20px",
    height: "80px",
    width: "140px",
    backgroundColor: "rgb(255, 255, 255)",
    ":hover": {
      backgroundColor: "rgb(230, 243, 255)",
      cursor: "pointer"
    },
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "center",
    marginBottom: 0
  },
  comingSoonPaperStyles: {
    padding: "20px",
    height: "80px",
    width: "140px",
    backgroundColor: "rgb(255, 255, 255)",
    ":hover": {
      backgroundColor: "rgb(230, 243, 255)",
      cursor: "not-allowed"
    }
  },
  favoriteList: {
    padding: "20px",
    marginTop: "10px",
    width: "100%",
    height: "fit-content",
    boxShadow: "0 0 0 0",
    ":hover": {
      backgroundColor: "rgb(230, 243, 255)"
    }
  },
  labelStyles: {
    fontSize: "16px",
    padding: "5px",
    borderRadius: "10px",
    backgroundColor: "rgb(255, 255, 255)",
    border: "1px solid rgb(242, 245, 247)",
    ":hover": {
      border: "1px solid rgb(201, 210, 217)"
    }
  },
  iconContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "rgb(0, 120, 212)",
    marginBottom: "0.5rem"
  },
  comingSoonBadge: {
    backgroundColor: "#0078D4",
    color: "#FFFFFF",
    padding: "2px 6px",
    borderRadius: "9999px",
    display: "inline-flex",
    alignItems: "center",
    fontSize: "12px",
    fontWeight: 500,
    position: "absolute",
    top: "5px",
    right: "5px"
  },
  newBadge: {
    marginTop: "14px",
    backgroundColor: "forestgreen",
    color: "#FFFFFF",
    padding: "4px 12px",
    borderRadius: "9999px",
    display: "inline-flex",
    alignItems: "center",
    fontSize: "12px",
    fontWeight: 500
  },

  icon: {
    color: "rgb(255,255,255)"
  },

  // Hover menu styles for Close the Loop tile
  tileWithMenuContainer: {
    position: "relative" as const,
    display: "inline-block"
  },
  hoverMenu: {
    position: "absolute" as const,
    top: "100%",
    left: "50%",
    transform: "translateX(-50%)",
    minWidth: "240px",
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 120, 212, 0.1)",
    padding: "8px",
    zIndex: 1000,
    marginTop: "8px",
    opacity: 0,
    visibility: "hidden" as const,
    transition: "opacity 0.2s ease, visibility 0.2s ease, transform 0.2s ease",
    border: "1px solid rgba(0, 120, 212, 0.15)"
  },
  hoverMenuVisible: {
    opacity: 1,
    visibility: "visible" as const,
    transform: "translateX(-50%) translateY(0)"
  },
  hoverMenuArrow: {
    position: "absolute" as const,
    top: "-8px",
    left: "50%",
    transform: "translateX(-50%)",
    width: 0,
    height: 0,
    borderLeft: "8px solid transparent",
    borderRight: "8px solid transparent",
    borderBottom: "8px solid #ffffff",
    filter: "drop-shadow(0 -2px 2px rgba(0,0,0,0.08))"
  },
  menuItem: {
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
    cursor: "pointer",
    borderRadius: "8px",
    transition: "all 0.15s ease",
    gap: "12px",
    backgroundColor: "transparent",
    ":hover": {
      backgroundColor: "rgba(0, 120, 212, 0.08)",
      transform: "translateX(4px)"
    }
  },
  menuItemIcon: {
    width: "36px",
    height: "36px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  },
  menuItemContent: {
    display: "flex",
    flexDirection: "column" as const,
    flex: 1,
    gap: "2px"
  },
  menuItemTitle: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#1a1a1a",
    lineHeight: 1.3
  },
  menuItemDescription: {
    fontSize: "11px",
    color: "#666666",
    lineHeight: 1.3
  },
  menuItemChevron: {
    color: "#999999",
    transition: "transform 0.15s ease"
  }
});

export interface TopCustomerProps {
  tenantId: string;
  tenantName: string;
}

export interface TopPartnersProps {
  mpnId: string;
  mpnName: string;
}

export interface TopProductProps {
  productId: string;
  productName: string;
}

export interface TopReportProps {
  reportId: string;
  report: string;
}

export interface SubMenuOption {
  label: string;
  description?: string;
  path?: string;
  externalUrl?: string;
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  iconColor: string;
  iconBgColor: string;
}

export interface TileProps {
  path?: string;
  externalUrl?: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  isBeta?: boolean;
  descriptionClassName?: string;
  subMenuOptions?: SubMenuOption[];
}

export interface NavigationTileProps {
  tile: TileProps;
  styles: ReturnType<typeof useStyles>;
  navigate: (path: string) => void;
}

type RecentlyViewedItem = TopCustomerProps | TopProductProps | TopReportProps;

const NavigationTile: React.FC<NavigationTileProps> = ({ tile, styles, navigate }) => {
  const handleClick = () => {
    if (tile.externalUrl) {
      window.open(tile.externalUrl);
    } else if (tile.path) {
      navigate(tile.path);
    }
  };

  const IconComponent = tile.icon;

  return (
    <Paper className={styles.paperStyles} onClick={handleClick}>
      {tile.isBeta && (
        <div className={styles.comingSoonBadge}>
          <New16Regular fontSize={12} style={{ marginRight: 4 }} />
          <span>Beta</span>
        </div>
      )}
      <div className={styles.tileStyles}>
        <div className={styles.iconContainer}>
          <IconComponent className={styles.icon} />
        </div>
        <Body1 className={tile.descriptionClassName || styles.tileDescription}>{tile.label}</Body1>
      </div>
    </Paper>
  );
};

// Component for tiles with hover menu (like Close the Loop with sub-options)
const NavigationTileWithMenu: React.FC<NavigationTileProps> = ({ tile, styles, navigate }) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const [showOnLeft, setShowOnLeft] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const IconComponent = tile.icon;

  const handleMouseEnter = () => {
    // Check if menu would overflow on the right side
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const menuWidth = 300; // approximate menu width including margin
      const viewportWidth = window.innerWidth;
      setShowOnLeft(rect.right + menuWidth > viewportWidth);
    }
    setIsHovered(true);
  };

  const handleMenuItemClick = (option: SubMenuOption) => {
    if (option.externalUrl) {
      window.open(option.externalUrl, "_blank", "noopener,noreferrer");
    } else if (option.path) {
      navigate(option.path);
    }
    setIsHovered(false);
  };

  return (
    <div
      ref={containerRef}
      className={styles.tileWithMenuContainer}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Paper
        className={styles.paperStyles}
        style={{
          cursor: "pointer",
          border: isHovered ? "2px solid #0078D4" : "2px solid transparent",
          transition: "border 0.2s ease",
          position: "relative"
        }}
      >
        {tile.isBeta && (
          <div className={styles.comingSoonBadge}>
            <New16Regular fontSize={12} style={{ marginRight: 4 }} />
            <span>Beta</span>
          </div>
        )}
        <div className={styles.tileStyles}>
          <div className={styles.iconContainer}>
            <IconComponent className={styles.icon} />
          </div>
          <Body1 className={tile.descriptionClassName || styles.tileDescription}>{tile.label}</Body1>
        </div>

        {/* Arrow indicator showing menu opens on hover */}
        <div
          style={{
            position: "absolute",
            ...(showOnLeft ? { left: "6px" } : { right: "6px" }),
            top: "50%",
            transform: isHovered
              ? `translateY(-50%) translateX(${showOnLeft ? "-2px" : "2px"})`
              : "translateY(-50%)",
            backgroundColor: isHovered ? "#0078D4" : "rgba(0, 120, 212, 0.1)",
            borderRadius: "50%",
            width: "20px",
            height: "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.2s ease"
          }}
        >
          {showOnLeft ? (
            <ChevronLeft16Regular
              style={{
                color: isHovered ? "#ffffff" : "#0078D4",
                fontSize: "14px",
                transition: "color 0.2s ease"
              }}
            />
          ) : (
            <ChevronRight16Regular
              style={{
                color: isHovered ? "#ffffff" : "#0078D4",
                fontSize: "14px",
                transition: "color 0.2s ease"
              }}
            />
          )}
        </div>
      </Paper>

      {/* Hover Menu - Dynamically positioned based on available space */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          ...(showOnLeft
            ? { right: "100%", left: "auto" }
            : { left: "100%", right: "auto" }
          ),
          transform: isHovered
            ? `translateY(-50%) translateX(${showOnLeft ? "-12px" : "12px"})`
            : "translateY(-50%) translateX(0px)",
          minWidth: "280px",
          backgroundColor: "#ffffff",
          borderRadius: "16px",
          boxShadow: "0 12px 40px rgba(0, 0, 0, 0.18), 0 4px 16px rgba(0, 120, 212, 0.12)",
          padding: "0",
          zIndex: 1000,
          opacity: isHovered ? 1 : 0,
          visibility: isHovered ? "visible" : "hidden",
          transition: "opacity 0.25s ease, visibility 0.25s ease, transform 0.25s ease",
          border: "1px solid rgba(0, 120, 212, 0.2)",
          overflow: "hidden"
        }}
      >
        {/* Arrow pointing to tile */}
        <div
          style={{
            position: "absolute",
            top: "50%",
            ...(showOnLeft
              ? { right: "-10px", left: "auto" }
              : { left: "-10px", right: "auto" }
            ),
            transform: "translateY(-50%)",
            width: 0,
            height: 0,
            borderTop: "10px solid transparent",
            borderBottom: "10px solid transparent",
            ...(showOnLeft
              ? { borderLeft: "10px solid #ffffff", borderRight: "none" }
              : { borderRight: "10px solid #ffffff", borderLeft: "none" }
            ),
            filter: `drop-shadow(${showOnLeft ? "2px" : "-2px"} 0px 2px rgba(0,0,0,0.1))`
          }}
        />

        {/* Header Section */}
        <div
          style={{
            background: "linear-gradient(135deg, #0078D4 0%, #106EBE 100%)",
            padding: "16px 18px",
            borderBottom: "1px solid rgba(0, 120, 212, 0.1)"
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <ArrowSync24Regular style={{ color: "#ffffff", fontSize: "22px" }} />
            <div>
              <div style={{ fontSize: "15px", fontWeight: 700, color: "#ffffff" }}>
                Close the Loop
              </div>
              <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.85)", marginTop: "2px" }}>
                Choose where to navigate
              </div>
            </div>
          </div>
        </div>

        {/* Menu Options */}
        <div style={{ padding: "8px" }}>
          {tile.subMenuOptions?.map((option, index) => {
            const OptionIcon = option.icon;
            return (
              <div
                key={`menu-option-${index}`}
                onClick={() => handleMenuItemClick(option)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleMenuItemClick(option);
                }}
                tabIndex={0}
                role="menuitem"
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "14px 16px",
                  cursor: "pointer",
                  borderRadius: "10px",
                  transition: "all 0.15s ease",
                  gap: "14px",
                  backgroundColor: "transparent",
                  marginBottom: index < (tile.subMenuOptions?.length || 0) - 1 ? "4px" : "0"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "rgba(0, 120, 212, 0.08)";
                  e.currentTarget.style.transform = "translateX(6px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.transform = "translateX(0)";
                }}
              >
                {/* Option Icon with colored background */}
                <div
                  style={{
                    width: "44px",
                    height: "44px",
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    backgroundColor: option.iconBgColor,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                  }}
                >
                  <OptionIcon style={{ color: option.iconColor, fontSize: "22px" }} />
                </div>

                {/* Option Content */}
                <div style={{ display: "flex", flexDirection: "column", flex: 1, gap: "3px" }}>
                  <span style={{ fontSize: "14px", fontWeight: 600, color: "#1a1a1a", lineHeight: 1.3 }}>
                    {option.label}
                  </span>
                  {option.description && (
                    <span style={{ fontSize: "11px", color: "#666666", lineHeight: 1.4 }}>
                      {option.description}
                    </span>
                  )}
                </div>

                {/* Chevron indicator */}
                <ChevronRight16Regular style={{ color: "#0078D4", flexShrink: 0 }} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default function HomePage() {
  useEffect(() => {
    logPageView("Home Page", window.location.href);
  }, []);

  /* * Removes duplicate entries from recently viewed items array.
   * - If `recentlyViewedItems` has items, it filters them to remove duplicates.
   * - If not provided, it defaults to an empty array.
   * @param recentlyViewedItems - Array of recently viewed items( customers/products/reports) or undefined.
   * @returns A new array with unique entries based on Id values. */

  const removeDuplicates = <T extends RecentlyViewedItem>(recentlyViewedItems: Array<T> | undefined): Array<T> => {
    if (!recentlyViewedItems) return [];
    const uniqueEntries = new Set<string>();

    const result = recentlyViewedItems.filter((each) => {
      let key: string | undefined;

      if ("tenantId" in each) {
        key = each.tenantId;
      } else if ("productId" in each) {
        key = each.productId;
      } else if ("reportId" in each) {
        key = each.reportId;
      }

      if (!key) return false;
      if (uniqueEntries.has(key)) {
        return false;
      }
      uniqueEntries.add(key);
      return true;
    });
    return result;
  };

  /* * Determines the title and data to show based on recently viewed items.
   * - If `RecentlyViewedItem` has items, it uses the recently viewed title and data.
   * - If not, it defaults to the fallback title and data.
   * @param RecentlyViewedItem - List of recently viewed items or undefined.
   * @param defaultData - Default data used when `RecentlyViewedItem` is empty or undefined.
   * @returns An object with the selected title and data. */
  const getTitleAndValues = <T extends RecentlyViewedItem>(
    recentlyViewedItems: Array<T> | undefined,
    defaultData: Array<T>,
    titleOptions: { recentlyViewedTitle: string; defaultTitle: string }
  ): { title: string; value: Array<T> } => {
    const hasData = recentlyViewedItems && recentlyViewedItems.length > 0;
    const title = hasData ? titleOptions.recentlyViewedTitle : titleOptions.defaultTitle;
    const value =
      hasData && Array.isArray(recentlyViewedItems)
        ? removeDuplicates(recentlyViewedItems)
        : Array.isArray(defaultData)
          ? defaultData
          : [];
    return { title, value };
  };

  const styles = useStyles();
  const navigate = useNavigate();
  // const [isOpen, { setTrue: openFeedbackPanel, setFalse: dismissPanel }] = useBoolean(false);
  const userName = sessionStorage.getItem("loginName")?.toString();
  const userEmail = sessionStorage.getItem("loginUserName")?.toString();
  const userLoginDetails = useMemo(
    () => ({
      loginEmail: userEmail ?? "",
      loginName: userName ?? ""
    }),
    [userName, userEmail]
  );
  const { data, isFetching } = useGetRecentlyViewedItemsQuery(userLoginDetails, {
    refetchOnMountOrArgChange: true
  });

  const { title: customerTitle, value: customerData } = getTitleAndValues<TopCustomerProps>(data?.recentlyViewedCustomers, topCustomers, {
    recentlyViewedTitle: "Recently Viewed Customers",
    defaultTitle: "Top Customers"
  });
  const { title: productTitle, value: productData } = getTitleAndValues<TopProductProps>(data?.recentlyViewedProducts, topProducts, {
    recentlyViewedTitle: "Recently Viewed Products",
    defaultTitle: "Top Products"
  });
  const { title: reportTitle, value: reportData } = getTitleAndValues<TopReportProps>(data?.recentlyViewedReports, topReports, {
    recentlyViewedTitle: "Recently Viewed Reports",
    defaultTitle: "Top Reports"
  });

  const uiEnablementData = useGetUIEnablementsSettingsQuery(undefined)?.data || [];
  const disabled = buildDisabledFeatureLookup(uiEnablementData);
  const disabledFeatures = disabled["feature"] || [];

  const announcements = [
    {
      id: 1,
      title: "Explore Products",
      description: "Dive Deep into Products",
      image: CarouselImage1,
      link: "/products"
    },
    {
      id: 2,
      title: "AI Hub",
      description: "Innovate with AI",
      image: CarouselImage2,
      link: "/aihub"
    },
    {
      id: 3,
      title: "Explore Report Catalog",
      description: "All Your Reports, One Place",
      image: CarouselImage3,
      link: "/explorereports"
    },
    {
      id: 4,
      title: "Explore Customers",
      description: "Your Customers, Fully Revealed",
      image: CarouselImage4,
      link: "/customersearch"
    },
    {
      id: 5,
      title: "Explore Health",
      description: "Customer Health at Your Fingertips",
      image: CarouselImage5,
      link: "/explorehealth"
    }
  ].filter(({ title }) => !disabledFeatures.map((item) => item.feature).includes(title));

  const navigationTiles: TileProps[] = [
    {
      path: "/customersearch",
      icon: People24Regular,
      label: "Explore Customers"
    },
    {
      path: "/products",
      icon: Apps24Regular,
      label: "Explore Products"
    },
    {
      path: "/partnersearch",
      icon: PeopleAudience24Regular,
      label: "Explore Partners",
      descriptionClassName: styles.newTileDescription
    },
    {
      path: "/explorehealth",
      icon: HeartPulse24Regular,
      label: "Explore Health",
      isBeta: true,
      descriptionClassName: styles.newTileDescription
    },
    {
      path: "/closetheloop",
      icon: ArrowSync24Regular,
      label: "Close the Loop",
      isBeta: true,
      descriptionClassName: styles.newTileDescription,
      subMenuOptions: [
        {
          label: "Customer Triage Hub",
          description: "Customer triage and management",
          externalUrl: process.env.REACT_APP_CTL_TRIAGE_HUB_URL || "https://cxe-triage-backend-g0b0bhfubyddajhe.westus-01.azurewebsites.net",
          icon: PeopleTeam24Regular,
          iconColor: "#ffffff",
          iconBgColor: "#0078D4"
        },
        {
          label: "Feature Similarity Search",
          description: "Find similar feature requests",
          externalUrl: process.env.REACT_APP_CTL_SIMILARITY_SEARCH_URL || "https://cxe-similarity-search.azurewebsites.net/",
          icon: BranchFork24Regular,
          iconColor: "#ffffff",
          iconBgColor: "#107C10"
        },
        {
          label: "CTL Reporting",
          description: "View Close the Loop analytics",
          path: "/closetheloop",
          icon: DataTrending24Regular,
          iconColor: "#ffffff",
          iconBgColor: "#8764B8"
        }
      ]
    },
    {
      path: "/aihub",
      icon: PictureInPicture24Regular,
      label: "AI Hub",
      isBeta: true,
      descriptionClassName: styles.newTileDescription
    },
    {
      path: "/explorereports",
      icon: DocumentText24Regular,
      label: "Explore Reports"
    },
    {
      path: "/explorereports?tab=supportCatalog",
      icon: PersonSupport24Regular,
      label: "Explore Support Catalog",
      isBeta: true,
      descriptionClassName: styles.newTileDescription
    },
    {
      externalUrl: "https://aka.ms/easycrm",
      icon: VideoPersonSparkle24Regular,
      label: "Easy CRM",
      isBeta: true,
      descriptionClassName: styles.newTileDescription
    }
  ].filter(({ label }) => !disabledFeatures.map((item) => item.feature).includes(label));

  const [currentSlide, setCurrentSlide] = React.useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % announcements.length);
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + announcements.length) % announcements.length);
  };

  const handleNextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % announcements.length);
  };

  const handleSlideClick = () => {
    const currentAnnouncement = announcements[currentSlide];
    if (currentAnnouncement && currentAnnouncement.link) {
      navigate(currentAnnouncement.link);
    }
  };

  // const panelStyles = {
  //   header: {
  //     paddingLeft: 18
  //   }
  // };

  return (
    <div
      style={{
        minWidth: 600,
        height: 1200,
        paddingLeft: 120,
        paddingRight: 60,
        backgroundColor: "rgb(251, 251, 251)"
      }}
    >
      <div
        style={{
          color: "rgb(0, 120, 212)",
          fontSize: 28,
          fontWeight: 700,
          marginTop: 10,
          marginBottom: 18,
          padding: "0px 0px 0px 25px"
        }}
      >
        {" "}
        Welcome, {sessionStorage.getItem("loginName")?.toString().split(" ")[0]}, to Security 360!
        <p
          style={{
            color: "black",
            fontSize: 18,
            fontWeight: 375
          }}
        >
          {/* Empowering MS Security CxE team to make data driven decision by understanding the adoption, usage and product
          stickiness of Security Portfolio */}
          Enabling data-driven decisions through adoption, usage, support, and feedback insights across Security Portfolio
        </p>
      </div>
      <Stack horizontal tokens={{ padding: 30, childrenGap: 20 }} wrap>
        <div className={styles.announcementSection}>
          <button
            className={`${styles.navigationButton} ${styles.prevButton}`}
            onClick={handlePrevSlide}
            aria-label="Previous slide"
          >
            <ChevronLeft24Regular />
          </button>
          <button
            className={`${styles.navigationButton} ${styles.nextButton}`}
            onClick={handleNextSlide}
            aria-label="Next slide"
          >
            <ChevronRight24Regular />
          </button>

          {announcements.map((announcement, index) => (
            <div
              key={announcement.id}
              onClick={() => handleSlideClick()}
              className={`${styles.slide} ${index === currentSlide ? styles.activeSlide : ""}`}
              style={{ backgroundImage: `url(${announcement.image})` }}
              role="button"
              tabIndex={0}
            >
              <div className={styles.slideContent}>
                <p className={styles.slideTitle}>{announcement.title}</p>
                <p className={styles.slideDescription}>{announcement.description}</p>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginLeft: 10, width: "48%", height: "fit-content", flexShrink: 0 }}>
          <Stack horizontal tokens={{ childrenGap: 10 }} wrap>
            {navigationTiles.map((tile, index) => (
              tile.subMenuOptions && tile.subMenuOptions.length > 0 ? (
                <NavigationTileWithMenu key={`nav-tile-${index}`} tile={tile} styles={styles} navigate={navigate} />
              ) : (
                <NavigationTile key={`nav-tile-${index}`} tile={tile} styles={styles} navigate={navigate} />
              )
            ))}
          </Stack>
        </div>
      </Stack>
      <FavoritesSection
        customers={customerData}
        customerTitle={customerTitle}
        isFetching={isFetching}
        products={productData}
        productTitle={productTitle}
        reports={reportData}
        reportTitle={reportTitle}
      />
      {/* <Panel
        headerText="Security360 Feedback"
        isOpen={isOpen}
        onDismiss={dismissPanel}
        isLightDismiss
        closeButtonAriaLabel="Close"
        styles={panelStyles}
      >
        <FeedbackBody />
      </Panel> */}
    </div>
  );
}
